import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { debounce as _debounce } from 'lodash';
import { Modal } from '../../components/popup';
import { IAppOnlyStore } from '../../stores';
import { action, reaction, observable, toJS, computed } from 'mobx';
import { IAnyUser, EUserTypeFilter, EUserPortalPermissionFilter } from '../../../../common/interfaces/users';
import { PortalSearchBarField } from '../../components/layout';
import { EUserType } from '../../../../common/interfaces/user';
import { StringMethod } from '../../../../common/helpers/string';
import { UserModalView } from '.';
import { IAutocompleteSuggestion } from '../../components/forms';
import { Button, EButtonType } from '../../components/action';

// Styles
const s = require('./UserModal.css');

// Enumerables
export enum EStatus {
    None,
    Saving,
    Success,
    Error
}

export enum EUserSaveStatus {
    None,
    Saving,
    Success,
    Error,
    ErrorCardCodeDoesNotExist
}

// Empty user
const emptyUser: IAnyUser = {
    id: null,
    username: '',
    email: '',
    emailConfirmed: false,
    name: '',
    isCustomer: false,
    isAdmin: false,
    isInternal: false,
    typeId: null,
    cardcode: '',
    slpcode: null,
    portalPermissionId: 1,
    isActive: true,
    lastLoggedAt: null,
    updatedAt: null,
    createdAt: null
};

// Props
interface IUserModalProps extends IAppOnlyStore {}

// Component
@inject('store')
@observer
export class UserModal extends React.Component<IUserModalProps, {}> {
    constructor(props: IUserModalProps) {
        super(props);

        this.handleGettingCustomerCardCodeSuggestions = _debounce(this.handleGettingCustomerCardCodeSuggestions, 300);
    }

    @observable private showUserDismissChangeModal: boolean = false;

    @observable private user: IAnyUser | null = emptyUser;
    @observable private userPassword: string = '';
    @observable private userPasswordRepeat: string = '';
    @observable private showTypePicker: boolean = false;
    @observable private showPermissionPicker: boolean = false;
    @observable private showStatusPicker: boolean = false;
    @observable private userIsModified: boolean = false;

    @observable private userPasswordResetStatus: EStatus = EStatus.None;
    @observable private userSaveStatus: EUserSaveStatus = EUserSaveStatus.None;

    @observable private showCustomerCardCodeAutocomplete: boolean = false;
    @observable private customerCardCodeAutocompleteStyle: React.CSSProperties = null;

    @computed
    private get userIsNew(): boolean {
        return this.user.id === null;
    }

    @computed
    private get userError(): string | null {
        if (this.user.username.trim().length <= 0) {
            return 'Username is required';
        }

        if (this.user.typeId === null) {
            return 'User type has to be selected';
        }

        if (this.user.name.trim().length <= 0) {
            return 'Full name is required';
        }

        if (this.user.isCustomer && this.user.cardcode.length <= 0) {
            return 'Card code is required';
        }

        if (this.user.email.trim().length <= 0) {
            return 'Email address is required';
        }

        if (StringMethod.isEmail(this.user.email) === false) {
            return 'Email address is not valid';
        }

        if (this.userIsNew) {
            if (this.userPassword.trim().length <= 0 || this.userPasswordRepeat.trim().length <= 0) {
                return 'Both password fields need to be filled';
            }

            if (this.userPassword !== this.userPasswordRepeat) {
                return 'Passwords do not match';
            }
        }

        return null;
    }

    private reactToModalStatusChange = reaction(
        () => this.props.store.users.userModalIsOpen,
        isOpen => {
            if (isOpen) {
                this.user =
                    this.props.store.users.userModalUserDetails !== null
                        ? toJS(this.props.store.users.userModalUserDetails)
                        : emptyUser;
            }
        }
    );

    public customerCardCodeFieldGroup: PortalSearchBarField = null;
    private referenceCustomerCardCodeFieldGroup = ref => (this.customerCardCodeFieldGroup = ref);

    public render() {
        return (
            <div>
                <UserModalView
                    users={this.props.store.users}
                    user={this.user}
                    userIsNew={this.userIsNew}
                    userError={this.userError}
                    userPassword={this.userPassword}
                    userPasswordRepeat={this.userPasswordRepeat}
                    showUserTypePicker={this.showTypePicker}
                    showPermissionPicker={this.showPermissionPicker}
                    showStatusPicker={this.showStatusPicker}
                    userIsModified={this.userIsModified}
                    userPasswordResetStatus={this.userPasswordResetStatus}
                    userSaveStatus={this.userSaveStatus}
                    onModalClose={this.handleModalClose}
                    onModalDisabledClose={this.handleModalDisabledClose}
                    onModalContainerClick={this.handleModalContainerClick}
                    onFormSubmit={this.handleFormSubmit}
                    onUserPortalPermissionsPickerClick={this.handleUserPortalPermissionsPickerClick}
                    onUserPortalPermissionChange={this.handleUserPortalPermissionChange}
                    onUserPortalPermissionFieldFocus={this.handleUserPortalPermissionFieldFocus}
                    onUserTypePickerClick={this.handleUserTypePickerClick}
                    onUserTypeChange={this.handleUserTypeChange}
                    onUserTypeFieldFocus={this.handleUserTypeFieldFocus}
                    onUserStatusPickerClick={this.handleUserStatusPickerClick}
                    onUserStatusChange={this.handleUserStatusChange}
                    onUserStatusFieldFocus={this.handleUserStatusFieldFocus}
                    onTextFieldChange={this.handleTextFieldChange}
                    onPasswordFieldChange={this.handlePasswordFieldChange}
                    onPasswordReset={this.handlePasswordReset}
                    referenceCustomerCardCodeFieldGroup={this.referenceCustomerCardCodeFieldGroup}
                    onCustomerCardCodeFieldClick={this.handleCustomerCardCodeFieldClick}
                    onCustomerCardCodeFocus={this.handleCustomerCardCodeFocus}
                    showCustomerCardCodeAutocomplete={this.showCustomerCardCodeAutocomplete}
                    customerCardCodeAutocompleteStyle={this.customerCardCodeAutocompleteStyle}
                    onCustomerCardCodeAutocompleteDismiss={this.handleCustomerCardCodeAutocompleteDismiss}
                    onCustomerCardCodeSuggestionsClick={this.handleCustomerCardCodeSuggestionsClick}
                    onCustomerCardCodeLoadMoreSuggestionsClick={this.handleCustomerCardCodeLoadMoreClick}
                />

                <Modal show={this.showUserDismissChangeModal} hideCloseButton={true} disableClose={true}>
                    <div className={s.dismissChangeModal}>
                        <p>Changes you've made to the user have not been saved. Are you sure you want lose them?</p>
                        <div>
                            <Button type={EButtonType.Action} inBox={true} onClick={this.handleUserDismissChangeModalKeepClick}>
                                Back to the user form
                            </Button>

                            <Button
                                type={EButtonType.Important}
                                inBox={true}
                                onClick={this.handleUserDismissChangeModalDismissClick}
                            >
                                Close and dismiss any changes
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    @action
    private handleModalContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.showTypePicker = false;
        this.showPermissionPicker = false;
        this.showCustomerCardCodeAutocomplete = false;
    };

    @action
    private handleUserTypePickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.showTypePicker = true;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleUserPortalPermissionsPickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.showPermissionPicker = true;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleUserStatusPickerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        this.showStatusPicker = true;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleUserTypeFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        this.showTypePicker = true;
        e.stopPropagation();
    };

    @action
    private handleUserPortalPermissionFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        this.showPermissionPicker = true;
        e.stopPropagation();
    };

    @action
    private handleUserStatusFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        this.showStatusPicker = true;
        e.stopPropagation();
    };

    @action
    private handleUserTypeChange = (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.userIsModified = true;
        this.user.typeId = newValue;
        this.user.isAdmin = newValue === 1;
        this.user.isCustomer = newValue === 2;
        this.user.isInternal = newValue === 3;
        this.user.cardcode = '';
        this.showTypePicker = false;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleUserPortalPermissionChange = (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.userIsModified = true;
        this.user.portalPermissionId = newValue;
        this.showPermissionPicker = false;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleUserStatusChange = (newValue: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.userIsModified = true;
        this.user.isActive = newValue;
        this.showStatusPicker = false;
        e.stopPropagation();
        e.preventDefault();
    };

    @action
    private handleTextFieldChange = (field: string, value: string) => {
        if (typeof this.user[field] !== 'undefined') {
            this.userIsModified = true;
            this.user[field] = value;

            if (field === 'cardcode') {
                this.showCustomerCardCodeAutocomplete = true;
                this.handleGettingCustomerCardCodeSuggestions();
            }
        }
    };

    @action
    private handlePasswordFieldChange = (field: string, value: string) => {
        if (typeof this[field] !== 'undefined') {
            this.userIsModified = true;
            this[field] = value;
        }
    };

    @action
    private handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (this.userError === null && this.userSaveStatus !== EUserSaveStatus.Saving) {
            this.userSaveStatus = EUserSaveStatus.Saving;

            if (this.userIsNew) {
                this.props.store.users
                    .createNewUser(this.user, this.userPassword)
                    .then(() => {
                        this.userSaveStatus = EUserSaveStatus.Success;
                        this.userIsModified = false;

                        setTimeout(() => {
                            this.handleModalClose();
                        }, 1500);
                    })
                    .catch(error => {
                        this.userSaveStatus =
                            typeof error.message !== 'undefined' && error.message && error.message === 'Cardcode does not exist'
                                ? EUserSaveStatus.ErrorCardCodeDoesNotExist
                                : EUserSaveStatus.Error;
                    });
            } else {
                this.props.store.users
                    .updateUserDetails(this.user)
                    .then(() => {
                        this.userSaveStatus = EUserSaveStatus.Success;
                        this.userIsModified = false;

                        setTimeout(() => {
                            this.handleModalClose();
                        }, 1500);
                    })
                    .catch(error => {
                        this.userSaveStatus =
                            typeof error.message !== 'undefined' && error.message && error.message === 'Cardcode does not exist'
                                ? EUserSaveStatus.ErrorCardCodeDoesNotExist
                                : EUserSaveStatus.Error;
                    });
            }
        }
    };

    @action
    private handlePasswordReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (this.userIsNew === false) {
            this.userPasswordResetStatus = EStatus.Saving;

            this.props.store.users
                .resetUserPassword(this.user.id)
                .then(() => {
                    this.userPasswordResetStatus = EStatus.Success;
                })
                .catch(() => {
                    this.userPasswordResetStatus = EStatus.Error;
                });
        }
    };

    private handleCustomerCardCodeFieldClick = (field: string, e: React.MouseEvent<HTMLLabelElement>) => {
        e.stopPropagation();
        this.showCustomerCardCodeAutocomplete = true;

        if (e.currentTarget) {
            this.customerCardCodeAutocompleteStyle = {
                left: e.currentTarget.offsetLeft + 'px',
                top: e.currentTarget.offsetTop + e.currentTarget.offsetHeight + 'px',
                width: e.currentTarget.offsetWidth + 'px'
            };
        }
    };

    private handleCustomerCardCodeFocus = (field: string, e: React.FocusEvent<HTMLInputElement>) => {
        this.showCustomerCardCodeAutocomplete = true;
    };

    @action
    private handleCustomerCardCodeAutocompleteDismiss = () => {
        this.showCustomerCardCodeAutocomplete = false;
    };

    @action
    private handleCustomerCardCodeSuggestionsClick = (
        suggestion: IAutocompleteSuggestion,
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        this.showCustomerCardCodeAutocomplete = false;
        this.user.cardcode = suggestion.value;
    };

    @action
    private handleGettingCustomerCardCodeSuggestions = () => {
        if (this.user.cardcode.trim().length > 0) {
            this.showCustomerCardCodeAutocomplete = true;
            this.props.store.users.getCustomers(this.user.cardcode, false);
        } else {
            this.showCustomerCardCodeAutocomplete = false;
        }
    };

    @action
    private handleCustomerCardCodeLoadMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.store.users.getCustomers(this.user.cardcode.trim(), true);
    };

    @action
    private handleModalClose = () => {
        this.props.store.users.closeUserModal();
        this.userIsModified = false;
    };

    @action
    private handleModalDisabledClose = () => {
        this.showUserDismissChangeModal = true;
    };

    @action
    private handleUserDismissChangeModalDismissClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.handleUserDismissChangeModalClose();

        setTimeout(() => {
            if (this.props.store.users.userModalIsOpen) {
                this.handleModalClose();
            }
        }, 512);
    };

    @action
    private handleUserDismissChangeModalKeepClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.handleUserDismissChangeModalClose();
    };

    @action
    private handleUserDismissChangeModalClose = () => {
        console.log('handleUserDismissChangeModalClose: ', false);
        this.showUserDismissChangeModal = false;
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/admin/UserModal.tsx